import { getListQueryLocale, getListQueryParamName } from 'src/_helpers/listQueryParams'

/**
 * Wspólne odczytywanie stanu listy (zakładka / sortowanie / strona) z parametrów adresu.
 *
 * Tych samych funkcji używa `usePagedRemoteList` po stronie klienta i komponenty serwerowe,
 * które renderują pierwszą stronę listy. Dzięki temu HTML z serwera od razu pokazuje właściwą
 * zakładkę i właściwe dane — bez „powidoku”, czyli mignięcia listy domyślnej przed docelową.
 */
const asArray = (value) => (Array.isArray(value) ? value : [])

export const getLocalizedParamValues = (values, locale) => {
  const localizedValues = values?.[getListQueryLocale(locale)]

  return localizedValues && typeof localizedValues === 'object' ? localizedValues : values
}

const paramValueToKey = (value, paramValues) =>
  Object.entries(paramValues || {}).find(([, paramValue]) => paramValue === value)?.[0]

export const resolveTabIdFromParamValue = (value, { tabs, paramValues }) => {
  if (!value) return null

  const knownIds = asArray(tabs).map((tab) => tab.id)
  const candidate = paramValueToKey(value, paramValues) ?? value

  return knownIds.includes(candidate) ? candidate : null
}

export const resolveSortValueFromParamValue = (value, { sortOptions, paramValues }) => {
  if (!value) return null

  const candidate = paramValueToKey(value, paramValues) ?? value

  return asArray(sortOptions).some((option) => option.value === candidate) ? candidate : null
}

export const resolvePageFromParamValue = (value) => {
  const rawPage = value === null || value === undefined ? '' : String(value)

  if (!/^\d+$/.test(rawPage)) return null

  const parsedPage = Number(rawPage)

  return Number.isSafeInteger(parsedPage) && parsedPage > 0 ? parsedPage : null
}

const getSearchParamValue = (searchParams, name) => {
  if (!name) return null

  const value = searchParams?.[name]

  return (Array.isArray(value) ? value[0] : value) ?? null
}

/**
 * Zwraca `null` dla każdej wartości, której nie ma w adresie (albo jest nieznana),
 * żeby wołający sam zdecydował, co jest jego domyślną zakładką/sortowaniem/stroną.
 */
export const readListStateFromSearchParams = ({
  searchParams,
  locale,
  tabs = null,
  tabParamValues = null,
  sortOptions = null,
  sortParamValues = null,
}) => ({
  tabId: resolveTabIdFromParamValue(getSearchParamValue(searchParams, getListQueryParamName('tab', locale)), {
    tabs,
    paramValues: getLocalizedParamValues(tabParamValues, locale),
  }),
  sortValue: resolveSortValueFromParamValue(getSearchParamValue(searchParams, getListQueryParamName('sort', locale)), {
    sortOptions,
    paramValues: getLocalizedParamValues(sortParamValues, locale),
  }),
  page: resolvePageFromParamValue(getSearchParamValue(searchParams, getListQueryParamName('page', locale))),
})
